<script setup>
// History tab — the signed-in user's recent quiz sessions, from
// GET /api/me/sessions (scoped to req.user.id server-side). Deck topic names
// come from /api/decks so we show "BRAKE Module 1" rather than the raw slug.
import { ref, onMounted } from "vue";

const loading = ref(true);
const error = ref("");
const sessions = ref([]);
const topicBySlug = ref({});

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
}
function fmtDuration(ms) {
  if (!ms || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}
function topic(slug) {
  if (!slug) return "(unknown deck)";
  return topicBySlug.value[slug] || slug;
}

onMounted(async () => {
  try {
    const [sRes, dRes] = await Promise.all([
      fetch("/api/me/sessions", { credentials: "include" }),
      fetch("/api/decks", { credentials: "include" })
    ]);
    if (!sRes.ok) throw new Error(`History request failed (${sRes.status})`);
    const sData = await sRes.json();
    sessions.value = sData.sessions || [];
    if (dRes.ok) {
      const dData = await dRes.json();
      topicBySlug.value = Object.fromEntries((dData.decks || []).map((d) => [d.slug, d.topic]));
    }
  } catch (e) {
    error.value = e.message || "Could not load your history.";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="card border-0 shadow-sm">
    <div class="card-body">
      <h2 class="h5 mb-3"><i class="bi bi-clock-history me-2"></i>Practice history</h2>

      <div v-if="loading" class="text-muted py-3">
        <span class="spinner-border spinner-border-sm me-2"></span>Loading your sessions…
      </div>

      <div v-else-if="error" class="alert alert-warning py-2 mb-0">{{ error }}</div>

      <p v-else-if="sessions.length === 0" class="text-muted mb-0">
        No sessions yet. Start practising from the <strong>Topics</strong> tab and your
        history will appear here.
      </p>

      <div v-else class="table-responsive">
        <table class="table table-sm align-middle mb-0">
          <thead>
            <tr class="text-muted small">
              <th scope="col">When</th>
              <th scope="col">Topic</th>
              <th scope="col" class="text-center">Questions</th>
              <th scope="col" class="text-center">Accuracy</th>
              <th scope="col" class="text-end">Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in sessions" :key="s.sessionId">
              <td>{{ fmtDate(s.startedAt) }}</td>
              <td>{{ topic(s.deckSlug) }}</td>
              <td class="text-center">{{ s.correct }} / {{ s.questions }}</td>
              <td class="text-center">
                <span
                  v-if="s.accuracy !== null"
                  class="badge"
                  :class="s.accuracy >= 70 ? 'bg-success' : s.accuracy >= 40 ? 'bg-warning text-dark' : 'bg-danger'"
                >{{ s.accuracy }}%</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end text-muted">{{ fmtDuration(s.durationMs) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
